import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import {
  getPlanShares,
  createPlanShare,
  updatePlanShare,
  deletePlanShare,
  type PlanShareRead,
  type PlanSharesResponse,
  type ShareCreateBody,
} from '@/api/shares'
import { queryKeys } from '@/lib/queryKeys'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'

/**
 * Hook for fetching plan shares
 */
export function usePlanShares(planId: string) {
  return useQuery<PlanSharesResponse>({
    queryKey: queryKeys.shares.byPlanId(planId),
    queryFn: () => getPlanShares(planId),
    enabled: !!planId,
  })
}

/**
 * Hook for creating a plan share
 */
export function useSharePlan(planId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: ShareCreateBody) => createPlanShare(planId, data),
    onSuccess: () => {
      // Invalidate shares query to refetch the list
      queryClient.invalidateQueries({
        queryKey: queryKeys.shares.byPlanId(planId),
      })
    },
  })
}

export interface ToggleShareFreezeVars {
  shareId: string
  canFreeze: boolean
}

/**
 * Optimistic freeze-grant toggle shared by the owner and admin share dialogs:
 * flips the checkbox immediately and rolls back if the request fails.
 */
export function shareFreezeToggleOptions(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  mutationFn: (vars: ToggleShareFreezeVars) => Promise<PlanShareRead>
) {
  return {
    mutationFn,
    onMutate: async ({ shareId, canFreeze }: ToggleShareFreezeVars) => {
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<PlanSharesResponse>(queryKey)
      if (previous) {
        queryClient.setQueryData<PlanSharesResponse>(queryKey, {
          ...previous,
          items: previous.items.map(item =>
            item.id === shareId ? { ...item, can_freeze: canFreeze } : item
          ),
        })
      }
      return { previous }
    },
    onError: (
      error: unknown,
      _vars: ToggleShareFreezeVars,
      context?: { previous?: PlanSharesResponse }
    ) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous)
      }
      toast.error(getErrorMessage(error, 'Failed to update freeze permission'))
    },
    onSuccess: (share: PlanShareRead) => {
      const name = share.user?.display_name || share.user?.email || 'User'
      toast.success(
        share.can_freeze
          ? `${name} can now freeze this plan`
          : `${name} can no longer freeze this plan`
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  }
}

/**
 * Owner-only: grant or revoke a collaborator's permission to freeze the plan
 */
export function useUpdatePlanShareFreeze(planId: string) {
  const queryClient = useQueryClient()

  return useMutation(
    shareFreezeToggleOptions(
      queryClient,
      queryKeys.shares.byPlanId(planId),
      ({ shareId, canFreeze }) =>
        updatePlanShare(planId, shareId, { can_freeze: canFreeze })
    )
  )
}

/**
 * Owner-only: remove a collaborator's access to the plan
 */
export function useRemovePlanShare(planId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ shareId }: { shareId: string }) =>
      deletePlanShare(planId, shareId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.shares.byPlanId(planId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.planDetail.metadata(planId),
      })
      toast.success('Access removed')
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to remove access'))
    },
  })
}
